import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccommodationStockState } from "../objects/accommodationStockState";
import { EMPTY_ACCOMMODATION_LIST_TOTAL, EMPTY_ACCOMMODATION_STOCK_LIST_CRITERIA, EMPTY_ACCOMMODATION_STOCK_LIST_FILTER, EMPTY_DISPLAYED_DATE } from "../constants/accommodationStockConstants";
import { AccommodationStock, AccommodationStockListCriteria, AccommodationStockListFilter, AccommodationStockTotal } from "../objects/accommodationStockList";
import { addDays, format } from "date-fns";
import { DisplayedDate } from "../objects/accommodationStockTable";
import { findFurthestEndDate, findNearestStartDate } from "../utils/dates/searchDates";
import { AccommodationStockUpdateCriteria } from "../objects/accommodationStockUpdate";
import { updateClientStockValue, updateRoomStockValue } from "../utils/accommodationStockUpdateStock";
import { calculAccommodationStockSubTotal, getAccommodationTotal } from "../utils/accommodationStockGetTotal";
import { filterAccommodationList } from "../utils/filterAccommodationList";

const initialState: AccommodationStockState = {
    list_criteria: EMPTY_ACCOMMODATION_STOCK_LIST_CRITERIA,
    refresh_accommodation_list: false,
    list_loading: false,
    list_filter: EMPTY_ACCOMMODATION_STOCK_LIST_FILTER,
    accommodation_list: [],
    original_accommodation_list: [],
    accommodation_list_total: EMPTY_ACCOMMODATION_LIST_TOTAL,
    displayed_dates: EMPTY_DISPLAYED_DATE,
    room_stock_criteria: null,
    client_stock_criteria: null,
    original_stocks: {},
    create_cart: false
};

const updateAccommodationStockListCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockListCriteria>): AccommodationStockState => ({
    ...state, list_criteria: action.payload,
});

const updateOriginalAccommodationStockListReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStock[]>): AccommodationStockState => ({
    ...state, original_accommodation_list: action.payload
})
const updateAccommodationStockListFilterReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockListFilter>): AccommodationStockState => {
    const filtered_accommodation_list = filterAccommodationList(state.original_accommodation_list, action.payload);
    return ({
        ...state, list_filter: action.payload, accommodation_list: calculAccommodationStockSubTotal(filtered_accommodation_list), accommodation_list_total: getAccommodationTotal(filtered_accommodation_list)
    });
};
const updateAccommodationStockListReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStock[]>): AccommodationStockState => {
    const filtered_accommodation_list = filterAccommodationList(action.payload, state.list_filter);
    return ({
        ...state, accommodation_list: calculAccommodationStockSubTotal(filtered_accommodation_list), accommodation_list_total: getAccommodationTotal(filtered_accommodation_list)
    });
};

const updateAccommodationListRoomStockReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria>): AccommodationStockState => ({
    ...state, original_accommodation_list: calculAccommodationStockSubTotal(updateRoomStockValue(action.payload, state.original_accommodation_list))
});
const updateAccommodationListClientStockReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria>): AccommodationStockState => ({
    ...state, original_accommodation_list: calculAccommodationStockSubTotal(updateClientStockValue(action.payload, state.original_accommodation_list))
});

const updateAccommodationStockListLoadingReducer = (state: AccommodationStockState, action: PayloadAction<boolean>): AccommodationStockState => ({
    ...state, list_loading: action.payload
});
const incraseDisplayedDatesReducer = (state: AccommodationStockState): AccommodationStockState => {
    let new_start_date = addDays(new Date(state.displayed_dates.end_date), 1);
    let new_end_date = addDays(new_start_date, 8);

    // Ensure new_end_date does not exceed event_site_date.end_date
    const max_end_date = state.list_criteria.event_site_date?.event_end_date ? addDays(new Date(state.list_criteria.event_site_date.event_end_date || ''), Number(state.list_criteria.event_site_date.nb_days_allowed_after_event || 0)) : null;
    if (max_end_date && (new_end_date > max_end_date)) {
        new_end_date = max_end_date;
        new_start_date = addDays(max_end_date, -8);
    }

    return {
        ...state,
        displayed_dates: {
            start_date: format(new_start_date, 'yyyy-MM-dd'),
            end_date: format(new_end_date, 'yyyy-MM-dd'),
        }
    };
};
const decraseDisplayedDatesReducer = (state: AccommodationStockState): AccommodationStockState => {
    let new_end_date = addDays(new Date(state.displayed_dates.start_date), -1);
    let new_start_date = addDays(new_end_date, -8);

    // Ensure new_start_date does not go below event_site_date.start_date
    const min_start_date = state.list_criteria.event_site_date?.event_start_date ? addDays(new Date(state.list_criteria.event_site_date.event_start_date || ''), -Number(state.list_criteria.event_site_date.nb_days_allowed_before_event || 0)) : null;
    if (min_start_date && (new_start_date < min_start_date)) {
        new_start_date = min_start_date;
        new_end_date = addDays(min_start_date, 8);
    }

    return {
        ...state,
        displayed_dates: {
            start_date: format(new_start_date, 'yyyy-MM-dd'),
            end_date: format(new_end_date, 'yyyy-MM-dd'),
        }
    };
};

const goOnDisplayedDatesReducer = (state: AccommodationStockState, action: PayloadAction<Date>): AccommodationStockState => {
    const end_date = (state.list_criteria?.event_site_date?.event_end_date) ? findFurthestEndDate(action.payload, addDays(new Date(state.list_criteria.event_site_date.event_end_date), Number(state.list_criteria.event_site_date.nb_days_allowed_after_event || 0))) : null;
    const start_date = (end_date && state.list_criteria?.event_site_date?.event_start_date) ? findNearestStartDate(end_date, addDays(new Date(state.list_criteria.event_site_date.event_start_date || ''), -Number(state.list_criteria.event_site_date.nb_days_allowed_before_event || 0))) : null;
    return {
        ...state,
        displayed_dates: {
            start_date: start_date?.toISOString() || new Date().toISOString(),
            end_date: end_date?.toISOString() || addDays(new Date(), 9).toISOString()
        }
    };
};

const setDisplayedDatesReducer = (state: AccommodationStockState, action: PayloadAction<DisplayedDate>): AccommodationStockState => ({
    ...state, displayed_dates: {
        start_date: format(new Date(action.payload.start_date), 'yyyy-MM-dd'),
        end_date: format(new Date(action.payload.end_date), 'yyyy-MM-dd'),
    }
});
const updateRoomStockCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria | null>): AccommodationStockState => ({
    ...state, room_stock_criteria: action.payload
});
const updateClientStockCriteriaReducer = (state: AccommodationStockState, action: PayloadAction<AccommodationStockUpdateCriteria | null>): AccommodationStockState => ({
    ...state, client_stock_criteria: action.payload
});

const setOriginalStocksReducer = (state: AccommodationStockState, action: PayloadAction<Record<number, number[]>>): AccommodationStockState => ({
    ...state, original_stocks: action.payload
});

const updateOneOriginalStockReducer = (state: AccommodationStockState, action: PayloadAction<{ index: number, value: number[] }>): AccommodationStockState => ({
    ...state, original_stocks: { ...state.original_stocks, [action.payload.index]: action.payload.value }
});

const setCreateCartReducer = (state: AccommodationStockState, action: PayloadAction<boolean>): AccommodationStockState => ({
    ...state, create_cart: action.payload
});

const refreshAccommodationStockReducer = (state: AccommodationStockState): AccommodationStockState => ({
    ...state, refresh_accommodation_list: !state.refresh_accommodation_list
});



export const accommodationStock = createSlice({
    name: 'accommodation_stock',
    initialState,
    reducers: {
        updateAccommodationStockListCriteria: updateAccommodationStockListCriteriaReducer,
        updateAccommodationStockListFilter: updateAccommodationStockListFilterReducer,
        updateAccommodationStockList: updateAccommodationStockListReducer,
        updateOriginalAccommodationStockList: updateOriginalAccommodationStockListReducer,
        updateAccommodationStockListLoading: updateAccommodationStockListLoadingReducer,
        incraseDisplayedDates: incraseDisplayedDatesReducer,
        decraseDisplayedDates: decraseDisplayedDatesReducer,
        setDisplayedDates: setDisplayedDatesReducer,
        goOnDisplayedDates: goOnDisplayedDatesReducer,
        updateRoomStockCriteria: updateRoomStockCriteriaReducer,
        updateClientStockCriteria: updateClientStockCriteriaReducer,
        updateAccommodationListRoomStock: updateAccommodationListRoomStockReducer,
        updateAccommodationListClientStock: updateAccommodationListClientStockReducer,
        setOriginalStocks: setOriginalStocksReducer,
        updateOneOriginalStock: updateOneOriginalStockReducer,
        setCreateCart: setCreateCartReducer,
        refreshAccommodationStock: refreshAccommodationStockReducer
    }
});

export const {
    updateAccommodationStockListCriteria,
    updateAccommodationStockListFilter,
    updateOriginalAccommodationStockList,
    updateAccommodationStockList,
    updateAccommodationStockListLoading,
    incraseDisplayedDates,
    decraseDisplayedDates,
    setDisplayedDates,
    goOnDisplayedDates,
    updateRoomStockCriteria,
    updateClientStockCriteria,
    updateAccommodationListRoomStock,
    updateAccommodationListClientStock,
    setOriginalStocks,
    updateOneOriginalStock,
    setCreateCart,
    refreshAccommodationStock
} = accommodationStock.actions;